import React, { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  FormHelperText,
  Autocomplete,
  Link,
  Typography,
  useTheme
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { PhotoCapture } from 'shared/components';
import { safeAtob } from 'shared/utils/securityUtils';

interface RegistrationFieldProps {
  field: any;
  value: any;
  onChange: (name: string, value: any) => void;
  onBlurEmail?: (email: string) => void;
  onBlurInvitationCode?: (code: string) => void;
  error?: string;
  successMessage?: string;
  companies?: any[];
  onTemporaryFileChange?: (fieldName: string, file: File | null) => void;
  onScrollToField?: (fieldName: string) => void;
}

export const RegistrationField: React.FC<RegistrationFieldProps> = ({
  field,
  value: rawValue,
  onChange,
  onBlurEmail,
  onBlurInvitationCode,
  error = '',
  successMessage = '',
  companies = [],
  onTemporaryFileChange,
  onScrollToField
}) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [processingCapture, setProcessingCapture] = useState<string | null>(null);

  const value = rawValue || '';

  switch (field.type) {
    case 'select':
      if (field.name === 'companyId') {
        return (
          <Autocomplete
            options={companies}
            getOptionLabel={(option) => option.name}
            value={companies.find(c => c.id === value) || null}
            onChange={(_, newValue) => onChange(field.name, newValue?.id || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                required={false}
                error={!!error}
                helperText={error || field.helperText}
                sx={{ minHeight: '72px' }}
              />
            )}
          />
        );
      }
      return (
        <TextField
          select
          fullWidth
          label={field.label}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          required={false}
          error={!!error}
          helperText={error || field.helperText}
          sx={{ minHeight: '72px' }}
        >
          {field.options?.map((option: any) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );

    case 'password': {
      const isConfirmPassword = field.name === 'confirmPassword';
      return (
        <TextField
          fullWidth
          type={(isConfirmPassword ? showConfirmPassword : showPassword) ? 'text' : 'password'}
          label={field.label}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          required={false}
          error={!!error}
          helperText={error || field.helperText}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => isConfirmPassword ? setShowConfirmPassword(!showConfirmPassword) : setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {(isConfirmPassword ? showConfirmPassword : showPassword) ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }
          }}
          sx={{ minHeight: '72px' }}
        />
      );
    }

    case 'checkbox':
      return (
        <Box sx={{ minHeight: '72px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => onChange(field.name, e.target.checked)}
                color="primary"
              />
            }
            label={
              field.linkTo ? (
                <Box component="span">
                  Acepto{' '}
                  <Link
                    href={field.linkTo}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="primary"
                  >
                    {field.name === 'acceptTerms' ? 'los términos y condiciones' : 'la política de privacidad'}
                  </Link>
                </Box>
              ) : field.label
            }
          />
          {error && (
            <FormHelperText error>{error}</FormHelperText>
          )}
        </Box>
      );

    case 'file': {
      // Determinar el tipo de captura basado en el nombre del campo
      let captureType: 'selfie' | 'document_front' | 'document_back';
      if (field.name === 'selfie') {
        captureType = 'selfie';
      } else if (field.name === 'idFront') {
        captureType = 'document_front';
      } else if (field.name === 'idBack') {
        captureType = 'document_back';
      } else {
        captureType = 'document_front'; // Default
      }

      return (
        <PhotoCapture
          key={field.name}
          type={captureType}
          label={field.label}
          value={value}
          error={error}
          helperText={field.helperText}
          required={field.required}
          onCapture={(imageData: string) => {
            // Prevenir loops - verificar si ya se está procesando este campo
            if (processingCapture === field.name) {
              return;
            }

            // Si imageData está vacío, limpiar el campo (botón close)
            if (!imageData || imageData === '') {
              setProcessingCapture(null);
              onChange(field.name, '');

              // Limpiar archivo temporal también
              onTemporaryFileChange?.(field.name, null);

              return;
            }

            // Verificar que imageData es válido
            if (typeof imageData !== 'string') {
              setProcessingCapture(null);
              return;
            }

            // Prevenir loops - verificar si ya se está procesando
            if (imageData.startsWith('blob:')) {
              setProcessingCapture(null);
              return;
            }

            // Marcar como procesando
            setProcessingCapture(field.name);

            try {
              // Validar que es base64 válido
              if (!imageData.startsWith('data:image/')) {
                setProcessingCapture(null);
                return;
              }

              // Convertir base64 a File
              let base64Data = imageData;

              // Si incluye el prefijo data:image/..., quitarlo
              if (imageData.includes(',')) {
                base64Data = imageData.split(',')[1];
              }

              // Validar que el base64 es válido
              if (!base64Data || base64Data.length === 0) {
                setProcessingCapture(null);
                return;
              }

              const byteCharacters = safeAtob(base64Data);
              if (!byteCharacters) {
                setProcessingCapture(null);
                return;
              }

              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const file = new File([byteArray], `${field.name}.jpg`, { type: 'image/jpeg' });

              // Almacenar temporalmente el archivo
              onTemporaryFileChange?.(field.name, file);

              // Solo actualizar el campo en el formulario para mostrar la preview
              onChange(field.name, file);

              // Hacer scroll automático al campo recién capturado
              setTimeout(() => {
                onScrollToField?.(field.name);
                setProcessingCapture(null);
              }, 100);

            } catch (error: unknown) {
              onChange(field.name, '');
              setProcessingCapture(null);
            }
          }}
        />
      );
    }

    default:
      if (field.name === 'phone') {
        return (
          <Box sx={{ minHeight: '72px' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {field.label} {field.required && '*'}
            </Typography>
            <PhoneInput
              country={'cr'}
              value={value}
              onChange={(phone) => onChange(field.name, '+' + phone)}
              disabled={(field as any).disabled === true}
              inputStyle={{
                width: '100%',
                height: '56px',
                fontSize: '16px',
                fontFamily: 'inherit',
                borderColor: error ? theme.palette.error.dark : 'rgba(0, 0, 0, 0.23)',
                borderRadius: '4px'
              }}
              containerStyle={{
                width: '100%'
              }}
              buttonStyle={{
                borderColor: error ? theme.palette.error.dark : 'rgba(0, 0, 0, 0.23)',
                borderRadius: '4px 0 0 4px'
              }}
              dropdownStyle={{
                zIndex: 1300
              }}
              enableSearch
              searchPlaceholder="Buscar país..."
              preferredCountries={['cr', 'us', 'mx', 'gt', 'sv', 'hn', 'ni', 'pa']}
              placeholder={field.placeholder}
            />
            {(error || successMessage || field.helperText) && (
              <FormHelperText error={!!error} sx={{
                mt: 0.5,
                ...(successMessage ? { color: 'success.main' } : {})
              }}>
                {error || successMessage || field.helperText}
              </FormHelperText>
            )}
          </Box>
        );
      }

      // Manejo especial para invitationCode
      if (field.name === 'invitationCode') {
        return (
          <Box sx={{ minHeight: '72px' }}>
            <>
              <TextField
                fullWidth
                type={field.type}
                label={field.label}
                value={value}
                onChange={(e) => onChange(field.name, e.target.value)}
                onBlur={(e) => onBlurInvitationCode?.(e.target.value)}
                required={false}
                error={!!error}
                helperText={!error ? (successMessage || field.helperText) : undefined}
                placeholder={field.placeholder}
                sx={{
                  mb: error ? 1 : 2,
                  ...(successMessage ? {
                    '& .MuiFormHelperText-root': {
                      color: 'success.main'
                    }
                  } : {})
                }}
              />
              {error && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  color: 'error.main'
                }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {error}
                  </Typography>
                </Box>
              )}
            </>
          </Box>
        );
      }

      return (
        <TextField
          fullWidth
          type={field.type}
          label={field.label}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          onBlur={
            field.name === 'email' ? (e) => onBlurEmail?.(e.target.value) :
            undefined
          }
          required={false}
          disabled={(field as any).disabled === true}
          error={!!error}
          helperText={error || successMessage || field.helperText}
          placeholder={field.placeholder}
          sx={{
            minHeight: '72px',
            ...(successMessage ? {
              '& .MuiFormHelperText-root': {
                color: 'success.main'
              }
            } : {})
          }}
        />
      );
  }
};
